import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { UserDataProvider, useUserData } from './context/UserDataContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SavePlanProvider, useSavePlan } from './context/SavePlanContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import ScrollToTop from './components/ScrollToTop';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import AuthModal from './components/auth/AuthModal';
import ProtectedRoute from './components/auth/ProtectedRoute';
import UnsavedBanner from './components/UnsavedBanner';
import SaveToast from './components/SaveToast';
import EnvironmentBadge from './components/EnvironmentBadge';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import AdminRoute from './pages/admin/AdminRoute';

// Home page loads immediately — no lazy (it's the LCP page)
import Home from './pages/Home';

// Import styles
import './styles/main.css';

// ─── Lazy-loaded pages (code-split into separate JS chunks) ───────────────────
// Each import() call creates a separate chunk — browser only downloads
// the chunk for the page the user actually visits.

// Static pages
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NotFound = lazy(() => import('./pages/NotFound'));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'));

// Auth & Plan Management
const MyPlans = lazy(() => import('./pages/MyPlans'));
const ComparePlans = lazy(() => import('./pages/ComparePlans'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const AuthConfirm = lazy(() => import('./pages/AuthConfirm'));

// Calculators
const QuickCalculator = lazy(() => import('./components/QuickCalculator'));
const ComprehensiveCalculator = lazy(() => import('./components/ComprehensiveCalculator'));
const StressTestCalculator = lazy(() => import('./components/StressTestCalculator'));
const SustainabilityCalculator = lazy(() => import('./components/SustainabilityCalculator'));
const RelocationCalculator = lazy(() => import('./components/RelocationCalculator'));
const Calculators = lazy(() => import('./pages/Calculators'));
const OptimizedRetirementPage = lazy(() => import('./pages/OptimizedRetirementPage'));
const ReverseMortgageCalculator = lazy(() => import('./pages/ReverseMortgageCalculator'));
const CorpusOptimizer = lazy(() => import('./pages/CorpusOptimizer'));
const InvestmentPlanner = lazy(() => import('./components/tools/InvestmentPlanner'));
const InvestmentFinder = lazy(() => import('./pages/InvestmentFinder'));

// Goal Calculators
const LoanClosureGoal = lazy(() => import('./components/goals/LoanClosureGoal'));
const EducationGoal = lazy(() => import('./components/goals/EducationGoal'));
const MarriageGoal = lazy(() => import('./components/goals/MarriageGoal'));
const LeisureGoal = lazy(() => import('./components/goals/LeisureGoal'));
const EmergencyGoal = lazy(() => import('./components/goals/EmergencyGoal'));
const ReplacementGoal = lazy(() => import('./components/goals/ReplacementGoal'));
const TotalFreedom = lazy(() => import('./components/goals/TotalFreedom'));
const RetirementGoalCalculator = lazy(() => import('./components/goals/RetirementGoalCalculator'));
const HomeDownPaymentGoal = lazy(() => import('./components/goals/HomeDownPaymentGoal'));

// Portfolio Tracker
const PortfolioDashboard = lazy(() => import('./pages/PortfolioDashboard'));
const AddInvestment = lazy(() => import('./pages/AddInvestment'));
const PortfolioXRay = lazy(() => import('./components/xray/PortfolioXRay'));
const FinancialHealthReport = lazy(() => import('./pages/FinancialHealthReport'));
const ImportSetupPage = lazy(() => import('./pages/ImportSetupPage'));
const ProjectedWealthCalculator = lazy(() => import('./pages/ProjectedWealthCalculator'));
const PortfolioUserGuide = lazy(() => import('./components/portfolio/PortfolioUserGuide'));
const PortfolioHealthReview = lazy(
  () => import('./components/PortfolioHealthReview/PortfolioHealthReview')
);
const ReportsPage = lazy(() => import('./pages/ReportsPage'));

// Tools
const MFEvaluator = lazy(() => import('./features/mf-evaluator/MFEvaluator'));
const InsuranceAdvisor = lazy(() => import('./pages/InsuranceAdvisor'));
const MarketPulsePage = lazy(() => import('./pages/MarketPulsePage'));
const ModelPortfolio = lazy(() => import('./pages/ModelPortfolio'));
const ETFPortfolio = lazy(() => import('./pages/ETFPortfolio'));
const InflationIndex = lazy(() => import('./pages/InflationIndex'));
const Sentinel = lazy(() => import('./pages/Sentinel'));

// TaxFreedom
const TaxFreedomLanding = lazy(() => import('./taxfreedom/pages/TaxFreedomLanding'));
const TaxValidator = lazy(() => import('./taxfreedom/pages/TaxValidator'));
const TaxSummary = lazy(() => import('./taxfreedom/pages/TaxSummary'));
const TaxHelp = lazy(() => import('./taxfreedom/pages/TaxHelp'));
const TaxDemoPage = lazy(() => import('./taxfreedom/pages/TaxDemoPage'));
const LossTrackerPage = lazy(() => import('./taxfreedom/pages/LossTrackerPage'));

// City Finder
const CityFinderHome = lazy(() => import('./pages/CityFinderHome'));
const CityDetail = lazy(() => import('./pages/CityDetail'));
const NeighborhoodList = lazy(() => import('./pages/NeighborhoodList'));
const NeighborhoodDetail = lazy(() => import('./pages/NeighborhoodDetail'));

// Learn
const LearnHome = lazy(() => import('./pages/LearnHome'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const AIImpactInsights = lazy(() => import('./pages/AIImpactInsights'));

// Annual Cash Flow Planner
const AnnualCashFlowPlanner = lazy(() => import('./planner/pages/AnnualCashFlowPlanner'));

// Admin & Misc
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const SentinelLeadersPage = lazy(() => import('./pages/admin/SentinelLeadersPage'));
const WebinarPage = lazy(() => import('./pages/WebinarPage'));
const LeapAILab = lazy(() => import('./pages/LeapAILab'));
const FinancialHealthCheck = lazy(() => import('./planning/FinancialHealthCheck'));

// ChatBot — lazy so it doesn't block initial page paint
const ChatBot = lazy(() => import('./components/ChatBot'));

// ─── Suspense fallback ───────────────────────────────────────────────────────

const PageSpinner = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      color: '#64748B',
      fontFamily: "'Inter', -apple-system, sans-serif",
      fontSize: 14,
    }}
  >
    Loading…
  </div>
);

// ─── Context bridges ─────────────────────────────────────────────────────────

const AuthUserDataBridge = () => {
  const { setLoadSavedPlan } = useAuth();
  const { loadSavedPlanIntoContext } = useUserData();

  useEffect(() => {
    // Set the callback in AuthContext to load plan into UserDataContext
    setLoadSavedPlan(() => loadSavedPlanIntoContext);
  }, [setLoadSavedPlan, loadSavedPlanIntoContext]);

  return null;
};

// Global UI components that need context
const GlobalUIComponents = () => {
  const { shouldShowBanner, showToast, toastMessage } = useSavePlan();
  const { setShowAuthModal } = useAuth();

  return (
    <>
      <UnsavedBanner
        isVisible={shouldShowBanner}
        onSaveClick={() => {
          localStorage.setItem(
            'finncal_preAuthState',
            JSON.stringify({
              returnPath: window.location.pathname,
              pendingSave: true,
              timestamp: Date.now(),
            })
          );
          setShowAuthModal(true);
        }}
      />
      <SaveToast isVisible={showToast} message={toastMessage} />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SavePlanProvider>
          <SubscriptionProvider>
            <UserDataProvider>
              <AuthUserDataBridge />
              <ScrollToTop />
              <div className="app">
                <Navigation />
                <AuthModal />
                <GlobalUIComponents />

                <main className="main-content">
                  <ChunkErrorBoundary>
                  <Suspense fallback={<PageSpinner />}>
                    <Routes>
                      {/* Home */}
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route path="/auth/confirm" element={<AuthConfirm />} />
                      <Route path="/" element={<Home />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />

                      {/* SEO comparison pages — content lives in src/data/comparisons.js */}
                      <Route path="/compare/:fullSlug" element={<ComparisonPage />} />

                      {/* Calculators */}
                      <Route path="/tools" element={<Navigate to="/tools/quick-check" replace />} />
                      <Route path="/tools/quick-check" element={<QuickCalculator />} />
                      <Route path="/tools/freedom-number" element={<ComprehensiveCalculator />} />
                      <Route path="/tools/stress-test" element={<StressTestCalculator />} />
                      <Route
                        path="/tools/corpus-sustainability"
                        element={<SustainabilityCalculator />}
                      />
                      <Route path="/tools/relocation" element={<RelocationCalculator />} />

                      {/* NEW: Optimized Retirement Page */}
                      <Route
                        path="/tools/optimized-retirement"
                        element={<OptimizedRetirementPage />}
                      />
                      {/* Backward compatibility for old URL */}
                      <Route
                        path="/tools/optimized-strategy"
                        element={<OptimizedRetirementPage />}
                      />

                      {/* NEW: Reverse Mortgage Calculator */}
                      <Route
                        path="/tools/reverse-mortgage"
                        element={<ReverseMortgageCalculator />}
                      />

                      {/* NEW: Corpus Optimizer Wizard */}
                      <Route path="/tools/corpus-optimizer" element={<CorpusOptimizer />} />

                      {/* Personal Inflation Index */}
                      <Route path="/tools/inflation-index" element={<InflationIndex />} />

                      {/* Goal Calculators */}
                      <Route
                        path="/tools/goals/retirement"
                        element={<RetirementGoalCalculator />}
                      />
                      <Route path="/tools/goals/education" element={<EducationGoal />} />
                      <Route path="/tools/goals/marriage" element={<MarriageGoal />} />
                      <Route path="/tools/goals/leisure" element={<LeisureGoal />} />
                      <Route path="/tools/goals/emergency" element={<EmergencyGoal />} />
                      <Route path="/tools/goals/replacement" element={<ReplacementGoal />} />
                      <Route
                        path="/tools/goals/home-downpayment"
                        element={<HomeDownPaymentGoal />}
                      />
                      <Route path="/tools/total-freedom" element={<TotalFreedom />} />
                      <Route path="/tools/goals/loan-closure" element={<LoanClosureGoal />} />
                      {/* NEW: Investment Planner Tool */}
                      <Route path="/tools/investment-planner" element={<InvestmentPlanner />} />

                      {/* Annual Cash Flow Planner */}
                      <Route path="/tools/planner" element={<AnnualCashFlowPlanner />} />

                      {/* NEW: Investment Question Tool */}
                      <Route path="/invest" element={<InvestmentFinder />} />

                      {/* Portfolio Tracker */}
                      <Route path="/tools/portfolio" element={<PortfolioDashboard />} />
                      <Route path="/tools/portfolio/add" element={<AddInvestment />} />
                      <Route path="/tools/portfolio/edit/:id" element={<AddInvestment />} />
                      <Route
                        path="/tools/portfolio/health-report"
                        element={<FinancialHealthReport />}
                      />
                      <Route path="/tools/portfolio/xray" element={<PortfolioXRay />} />
                      <Route path="/tools/portfolio/import-setup" element={<ImportSetupPage />} />
                      <Route
                        path="/tools/portfolio/projected-wealth"
                        element={<ProjectedWealthCalculator />}
                      />
                      <Route path="/tools/portfolio/guide" element={<PortfolioUserGuide />} />
                      <Route
                        path="/tools/portfolio/health-review"
                        element={<PortfolioHealthReview />}
                      />
                      <Route path="/tools/reports" element={<ReportsPage />} />

                      {/* Investment Calculators (SEO landing page) */}
                      <Route path="/calculators" element={<Calculators />} />

                      {/* MF Evaluator */}
                      <Route path="/tools/mf-evaluator" element={<MFEvaluator />} />

                      {/* Model Portfolio */}
                      <Route path="/tools/model-portfolio" element={<ModelPortfolio />} />

                      {/* ETF Portfolio — Masterclass subscribers */}
                      <Route path="/tools/etf-portfolio" element={<ETFPortfolio />} />

                      {/* Insurance Advisor */}
                      <Route path="/tools/insurance" element={<InsuranceAdvisor />} />
                      <Route path="/tools/market-pulse" element={<MarketPulsePage />} />

                      {/* Sentinel — early trend detection, per-user access_sentinel flag */}
                      <Route path="/tools/sentinel" element={<Sentinel />} />

                      {/* TaxFreedom */}
                      <Route path="/tools/tax-freedom" element={<TaxFreedomLanding />} />
                      <Route
                        path="/tools/tax-freedom/validate"
                        element={
                          <ProtectedRoute>
                            <TaxValidator />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/tools/tax-freedom/summary/:id"
                        element={
                          <ProtectedRoute>
                            <TaxSummary />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/tools/tax-freedom/help" element={<TaxHelp />} />
                      <Route path="/tools/tax-freedom/demo" element={<TaxDemoPage />} />
                      <Route
                        path="/tools/tax-freedom/loss-tracker"
                        element={
                          <ProtectedRoute>
                            <LossTrackerPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* City Finder */}
                      <Route path="/city-finder" element={<CityFinderHome />} />
                      <Route path="/city-finder/:cityId" element={<CityDetail />} />
                      <Route
                        path="/city-finder/:cityId/neighborhoods"
                        element={<NeighborhoodList />}
                      />
                      <Route
                        path="/city-finder/:cityId/:neighborhoodId"
                        element={<NeighborhoodDetail />}
                      />

                      {/* Learn Section */}
                      <Route path="/learn" element={<LearnHome />} />
                      {/* LeapAI Lab - Admin Only */}
                      <Route
                        path="/learn/leapai-lab"
                        element={
                          <AdminRoute>
                            <LeapAILab />
                          </AdminRoute>
                        }
                      />
                      <Route path="/learn/:category" element={<CategoryPage />} />
                      <Route
                        path="/learn/market-insights/ai-impact-on-portfolio"
                        element={<AIImpactInsights />}
                      />
                      <Route path="/learn/:category/:slug" element={<ArticlePage />} />

                      {/* Feedback removed - redirect to contact */}
                      <Route path="/feedback" element={<Navigate to="/contact" replace />} />

                      {/* Auth & Plan Management */}
                      <Route
                        path="/my-plans"
                        element={
                          <ProtectedRoute>
                            <MyPlans />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/compare-plans"
                        element={
                          <ProtectedRoute>
                            <ComparePlans />
                          </ProtectedRoute>
                        }
                      />

                      {/* Pricing */}
                      <Route path="/pricing" element={<PricingPage />} />

                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/disclaimer" element={<Disclaimer />} />
                      <Route path="/settings" element={<SettingsPage />} />

                      {/* Webinar public page */}
                      <Route path="/webinar/:slug" element={<WebinarPage />} />

                      {/* Admin Panel */}
                      <Route
                        path="/admin"
                        element={
                          <AdminRoute>
                            <AdminDashboard />
                          </AdminRoute>
                        }
                      />

                      {/* SENTINEL Leaders — standalone admin research view (spec §9) */}
                      <Route
                        path="/admin/sentinel-leaders"
                        element={
                          <AdminRoute>
                            <SentinelLeadersPage />
                          </AdminRoute>
                        }
                      />

                      {/* Financial Planning Suite (Beta) */}
                      <Route path="/financial-health-check" element={<FinancialHealthCheck />} />

                      {/* 404 */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                  </ChunkErrorBoundary>
                </main>

                <Footer />
                {/* ChatBot — lazy so it doesn't block initial page paint */}
                <Suspense fallback={null}>
                  <ChatBot />
                </Suspense>
                <EnvironmentBadge />
              </div>
            </UserDataProvider>
          </SubscriptionProvider>
        </SavePlanProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
